import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { Item, MediaType, MenuItem, ResourceType } from '#/client';
import { NavigationType } from '#/service/constants';
import { snakeToCamel } from '#/service/utils';
import { tb, useTranslations } from '#/store/translation/translation.store';

import { useConfig } from './useConfig';

export interface SeoInfo {
  title?: string;
  localizedTitle?: string;
  item?: Item;
  serviceTitle?: string;
}

// CORE currently uses following title as fallback therefore we leverage it here as well.
const fallbackTitle = 'Accedo One - Web';

/**
 * Sets the page title based on the current route and given params object.
 * When the given params contain empty properties then we will try to
 * extract meaningful information from the route and the menu's configuration
 *
 */
export const useSeoInfo = ({ title, localizedTitle, item }: SeoInfo) => {
  const location = useLocation();
  const {
    menu: { mainMenuItems },
    featureConfig: { seo },
  } = useConfig();
  const { translations } = useTranslations();

  useEffect(() => {
    // On hard refresh or reloads the translations might not be loaded yet. therefore we need to wait for them.
    if (Object.keys(translations).length === 0) {
      return;
    }

    document.title =
      getTitle(
        mainMenuItems,
        location.pathname,
        title,
        localizedTitle,
        item,
        seo.serviceTitle
      ) ||
      seo.serviceTitle ||
      fallbackTitle;
  }, [
    translations,
    title,
    localizedTitle,
    item,
    seo.serviceTitle,
    location.pathname,
    mainMenuItems,
  ]);
};

const getTitle = (
  mainMenuItems: MenuItem[],
  pathname: string,
  title: SeoInfo['title'],
  localizedTitle: SeoInfo['localizedTitle'],
  item: SeoInfo['item'],
  serviceTitle: SeoInfo['serviceTitle']
) => {
  const context = {
    serviceTitle,
    item,
    page: {
      localizedTitle: getLocalizedTitle(
        mainMenuItems,
        pathname,
        title,
        localizedTitle
      ),
    },
  };

  const translationKey = getTranslationKey(context);

  const itemType =
    item?.type === MediaType.Item ? ResourceType.Item : ResourceType.Collection;

  context.serviceTitle ||= context.page.localizedTitle;
  context[itemType] = item;

  return tb(translationKey, {
    ...context,
    // disable escaping to prevent eg apostrophies in the title turning into `&amp;`.
    interpolation: { escapeValue: false },
  });
};

/**
 * Get the localized title of the current page.
 * @param menu Menu from the configuration
 * @param pathname Current pathname from the `location`
 * @param title Custom title from internal pages
 * @param localizedTitle Current page title from the desktop layout template
 * @returns title of the currently active page
 */
const getLocalizedTitle = (
  menu: MenuItem[],
  pathname: string,
  title: SeoInfo['title'],
  localizedTitle: SeoInfo['localizedTitle']
) => {
  // Ignore localized title if the user enteres home page - should display service title only.
  if (pathname === '/') {
    return undefined;
  }

  // Use custom page title if it exits.
  if (title) {
    return title;
  }

  // Use localized title if it exists, otherwise fallback to the menu item title.
  return localizedTitle || getCurrentMenuTitle(menu, pathname);
};

const getTranslationKey = (context: any) => {
  const { item, page, serviceTitle } = context;

  // If we have an item, we use the constructed item or collection title.
  if (item && serviceTitle) {
    return `web.seo.title.${item.type.toLowerCase()}.${snakeToCamel(
      item.subtype
    )}`;
  }

  // If we have a localized title or custom title with a service title, we use the default page title.
  if (page.localizedTitle && serviceTitle) {
    return 'web.seo.title.page.default';
  }

  // If we have nothing, we use the default title if a service title exists.
  return 'web.seo.title.default';
};

/**
 * Gets title of the currently active page from the menu, so it's available for SEO purposes
 * @param menu Menu from the configuration
 * @param pathname Current pathname from the `location`
 * @param ignoreHome @default true - whether to ignore home page
 * @returns title of the currently active page
 */
function getCurrentMenuTitle(
  menu: MenuItem[],
  pathname: string,
  ignoreHome = true
) {
  if (!menu || !pathname) {
    return undefined;
  }

  let normalizedPathname = pathname.toLowerCase();

  if (!ignoreHome && menu.length > 0 && pathname === '/') {
    const dest = menu[0].action.params?.destination;
    normalizedPathname = `/${dest?.type}/${dest?.id}`.toLowerCase();
  }

  const currentMenu = menu.find((e) => {
    const dest = e.action.params?.destination;
    const menuPath =
      dest?.type === NavigationType.Page
        ? `/${dest.type}/${dest.id}`
        : `/${dest?.id}`;

    return menuPath.toLowerCase() === normalizedPathname;
  });

  return currentMenu?.display.title;
}
